import { FC, useState } from 'react';
import { HStack } from '@chakra-ui/react';
import { format, startOfDay, subMonths } from 'date-fns';

import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import BackButton from 'components/BackButton';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import { StepProps } from 'components/Stepper/types';
import * as Layout from 'layouts/StepperLayout';

import { periodLocales } from '../../../../common/periodLocales';
import { useExportCreationContext } from '../../../../hooks/useExportCreationContext';
import { TimeFrameType } from '../../../../types';
import locales from './locales';
import {
  formatTimeFrameDate,
  getCurrentMonthText,
  getLastMonthText,
  getLastThreeMonthsText,
  getTimeFrameDates,
} from './utils';

const SelectPeriod: FC<StepProps> = ({ goBack, goNext }) => {
  const { setSharedState, sharedState } = useExportCreationContext();

  const [timeframeType, setTimeframeType] = useState(sharedState.timeframeType);
  const [startDate, setStartDate] = useState(
    startOfDay(subMonths(new Date(), 3)),
  );
  const [endDate, setEndDate] = useState(new Date());

  const handleNext = () => {
    if (timeframeType) {
      if (timeframeType === TimeFrameType.CUSTOM_TIMEFRAME) {
        setSharedState((prevState) => ({
          ...prevState,
          endDate: formatTimeFrameDate(endDate),
          startDate: formatTimeFrameDate(startDate),
          timeframeType,
        }));
      } else {
        const { endDate: endDateTimeFrame, startDate: startDateTimeFrame } =
          getTimeFrameDates(timeframeType);

        setSharedState((prevState) => ({
          ...prevState,
          endDate: endDateTimeFrame,
          startDate: startDateTimeFrame,
          timeframeType,
        }));
      }
    }

    goNext();
  };

  const periodOptions = [
    {
      description: getCurrentMonthText(new Date()),
      label: periodLocales.current_month,
      value: TimeFrameType.CURRENT_MONTH,
    },
    {
      description: getLastMonthText(new Date()),
      label: periodLocales.last_month,
      value: TimeFrameType.LAST_MONTH,
    },
    {
      description: getLastThreeMonthsText(new Date()),
      label: periodLocales.last_3_months,
      value: TimeFrameType.LAST_3_MONTHS,
    },
    {
      description: format(new Date(), 'yyyy'),
      label: periodLocales.current_year,
      value: TimeFrameType.CURRENT_YEAR,
    },
    {
      description:
        timeframeType === TimeFrameType.CUSTOM_TIMEFRAME ? (
          <>
            <SunshineCard.Text>{locales.custom_period}</SunshineCard.Text>

            <HStack paddingTop="space-8" spacing="space-40">
              <Form.SunshineInputDatePicker
                inputProps={{
                  placeholder: locales.from,
                }}
                modifiers={{ disabled: { after: endDate } }}
                onChange={(date) => setStartDate(date ?? new Date())}
                value={startDate}
              />

              <Form.SunshineInputDatePicker
                inputProps={{
                  placeholder: locales.to,
                }}
                modifiers={{ disabled: { before: startDate } }}
                onChange={(date) => setEndDate(date ?? new Date())}
                value={endDate}
              />
            </HStack>
          </>
        ) : (
          locales.custom_period
        ),
      label: locales.pick_timeframe,
      value: TimeFrameType.CUSTOM_TIMEFRAME,
    },
  ];

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton onClick={goBack} />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <Heading paddingBottom="space-32" size="lg">
            {locales.title}
          </Heading>

          <SunshineCard.RadioGroup
            onChange={(period: TimeFrameType) => setTimeframeType(period)}
            value={timeframeType}
          >
            <SunshineCard.Group>
              {periodOptions.map((period) => (
                <SunshineCard
                  key={period.value}
                  title={period.label}
                  value={period.value}
                  variant="radio"
                >
                  <SunshineCard.Content>
                    <SunshineCard.Text>{period.description}</SunshineCard.Text>
                  </SunshineCard.Content>
                </SunshineCard>
              ))}
            </SunshineCard.Group>
          </SunshineCard.RadioGroup>
        </Layout.BodyScrollableSection>

        <Layout.FooterContainer>
          <StepperProgressBar />

          <Layout.FooterButtonsContainer>
            <Button
              icon="arrow-right"
              isDisabled={!timeframeType}
              onClick={handleNext}
            >
              {locales.next}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default SelectPeriod;
